import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  ReceiptText,
  UserCog,
  LogOut,
} from "lucide-react";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/deposit", label: "Deposit", icon: ArrowDownToLine, id: "nav-deposit" },
  { to: "/withdrawal", label: "Withdrawal", icon: ArrowUpFromLine, id: "nav-withdrawal" },
  { to: "/history", label: "Transactions", icon: ReceiptText, id: "nav-history" },
  { to: "/profile", label: "Profile & Security", icon: UserCog, id: "nav-profile" },
];

export const UserLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex w-64 flex-col bg-[#09090B] text-[#FAFAFA] fixed h-screen border-r border-white/10">
        <div className="px-6 py-7 border-b border-white/10">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight">
            Swiftors<span className="text-[#059669]">.</span>
          </h1>
          <p className="overline mt-1 text-white/40">Payment Panel</p>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-white text-[#09090B] font-medium"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon size={17} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/40 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#09090B] text-white flex items-center justify-between px-4 h-14 border-b border-white/10">
        <h1 className="font-heading font-extrabold text-lg">Swiftors<span className="text-[#059669]">.</span></h1>
        <button data-testid="mobile-logout-btn" onClick={doLogout} className="text-white/70"><LogOut size={18} /></button>
      </div>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#09090B] text-white flex justify-around py-2 border-t border-white/10">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[9px] px-2 ${isActive ? "text-white" : "text-white/50"}`}>
            <l.icon size={18} />
            {l.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-8">{children}</div>
      </main>
    </div>
  );
};
